import { useTranslation } from 'react-i18next'

import { type ControlSize } from '@/components/ui/control'
import { CounterpartyPicker } from '@/features/counterparties/components/CounterpartyPicker'
import { CARRIER_CATEGORIES } from '@/features/counterparties/counterpartyList'

/* Who is moving a leg or a truck — a counterparty picked from the directory
 * rather than typed (S-22b).
 *
 * ── Which categories, and why only three ──
 *
 * A leg is carried by a CARRIER (the company whose truck it is), a FORWARDER
 * that sub-contracted the movement, or an owner-DRIVER. A customs broker, an
 * agent and «Прочее» are parties to the shipment rather than movers of it, and
 * offering them would make "carrier" mean "any company mentioned on this
 * case" — what the source workbook's single conflated column meant.
 * «Перевозчик» stays the ROLE word on the leg whichever of the three fills it.
 *
 * The CATEGORY is shown on every row, because one name can be two records — a
 * haulier and the forwarding desk of the same group.
 *
 * ── What is offered, and what merely stays selected ──
 *
 * The list offers ACTIVE counterparties (the list read's default). A
 * deactivated one is one the desk has decided not to work with again, and a
 * chooser that kept offering it would make deactivation cosmetic.
 *
 * The value already on the record is nonetheless kept SELECTED, because nothing
 * here may reject data that is already saved:
 *
 *   * a linked counterparty that is now inactive, outside these three
 *     categories, or missing because the list came back EMPTY — a reader
 *     without `counterparty.read` gets `[]`, and a custom role holding the
 *     case's identity read but not the counterparty read must still save a leg
 *     with its `carrierId` intact. The trigger shows the leg's stored name, and
 *     the id leaves exactly as it came: this control writes only on a pick.
 *   * a free-text carrier matching no counterparty at all — every leg recorded
 *     before migration 0046, and every carrier written off a waybill before
 *     anybody created a record for them. It is offered as its own first row and
 *     marked "as entered", which is exactly what CountryDropdown does with a
 *     port name stored before that field became a picker.
 *
 * ── The add-a-carrier escape hatch ──
 *
 * Inside the picker rather than beside it, and it is the difference between
 * this control being used and being worked around. The carrier standing in
 * front of somebody at 7pm is frequently not in the directory yet, and a picker
 * that makes them abandon the leg, cross to the directory screen and start the
 * leg again is a picker whose users save legs with no carrier. The leg sheet
 * stays open and holding its draft throughout — the counterparty form is a
 * second sheet over the first, not a navigation. Offered only to a reader who
 * may create one (`counterparty.read` + `counterparty.create`): a button the
 * server would refuse is not shown (frontend/README).
 */

export interface CarrierSelectProps {
  /** The linked counterparty's id, or '' when the leg is linked to nobody. */
  value: string
  /** The carrier's name as the record carries it — the linked counterparty's,
   *  or the free text from before the link. What the trigger shows when the
   *  list has no row for `value`, and what the "as entered" row offers. */
  name: string
  /** Both halves move together, always. A caller that could set the id without
   *  the name would be a caller that can leave the two disagreeing, and the
   *  name is what a leg falls back to when the directory row is later deleted. */
  onChange: (carrier: { id: string; name: string }) => void
  id?: string
  disabled?: boolean
  size?: ControlSize
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  /** The control's name where no `<label>` is bound to `id` — a carrier cell in
   *  a grid row («Перевозчик, фура 2») names itself (S-10). */
  'aria-label'?: string
}

/* Base UI: none directly — `CounterpartyPicker` (S-22d) is the strict picker,
   composed from Combobox.tsx; this wrapper only fixes its categories and its
   words. Its props, behaviour and tests did not change in the extraction. */
export function CarrierSelect({
  value,
  name,
  onChange,
  id,
  disabled,
  size = 'md',
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-label': ariaLabel,
}: CarrierSelectProps) {
  const { t } = useTranslation()

  return (
    <CounterpartyPicker
      value={value}
      name={name}
      onChange={onChange}
      /* One read of the three categories — the endpoint takes `category`
         repeated, so brokers and agents are never fetched only to be dropped
         here. The create sheet offers the same three. */
      categories={CARRIER_CATEGORIES}
      strings={{
        placeholder: t('cases.legs.carrierPlaceholder'),
        search: t('cases.legs.carrierSearch'),
        asEntered: t('cases.legs.carrierAsEntered'),
        inactive: t('cases.legs.carrierInactive'),
        add: t('cases.legs.carrierAdd'),
        /* Titled as what the button said it adds — a carrier (QW-2, D-4). */
        createTitle: t('cases.legs.carrierNew'),
      }}
      id={id}
      disabled={disabled}
      size={size}
      aria-describedby={describedBy}
      aria-invalid={invalid}
      aria-label={ariaLabel}
    />
  )
}
